import { useId, useState, type FormEvent } from 'react'
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react'
import { useProductShippingFacts, useProductShippingQuote } from '@/api/hooks'
import type { components } from '@/api/schema'
import { LoadingButton } from '@/components/loading-button'
import { Badge } from '@/components/ui/badge'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  addBusinessDays,
  dayNumber,
  todayNumber,
  weekdayLabel,
} from '@/features/projects/project-expected-timeline-model'
import { getApiErrorMessage } from '@/lib/api-error'
import { cn, formatMoney, formatNumber, formatStatus } from '@/lib/utils'
import { ProductDeliveryTimeline, type TimelineStep } from './product-delivery-timeline'

type Facts = components['schemas']['ProductShippingFactsRead']
type Estimate = components['schemas']['ProductDeliveryEstimateRead']
type Service = components['schemas']['ProductShippingServiceRead']
type LeadTime = components['schemas']['ProductLeadTimeRead']
type SourceValue = Record<string, unknown>

function record(value: unknown): SourceValue {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as SourceValue)
    : {}
}

/** Where one package fact comes from, in plain words. */
function sourceLabel(source: unknown): string {
  const labels: Record<string, string> = {
    override: 'Set by staff',
    exact: 'Product data',
    exact_or_snapshot: 'Product data',
    product: 'Product data',
    taxonomy_profile: 'Similar products',
    inferred: 'Similar products',
    peer: 'Similar products',
    taxonomy_density_from_weight: 'Estimated from weight',
    taxonomy_density_from_dimensions: 'Estimated from size',
    taxonomy_profile_density: 'Estimated from weight',
    price_peers: 'Similar-price products',
    manufacturer_peers: 'Same manufacturer',
    inferred_from_weight: 'Estimated from weight',
    inferred_from_dimensions: 'Estimated from size',
    derived_from_volume: 'Estimated from size',
    unit_longest_side: 'Estimated from size',
    density: 'Calculated from density',
    conservative_default: 'Default (missing)',
  }
  return labels[String(source)] ?? formatStatus(String(source || 'unknown'))
}

/** Where the product ships from. A manufacturer address we never saved as a ship-from used to read "Runtime Fallback". */
const ORIGIN_LABELS: Record<string, string> = {
  manufacturer_primary: 'Manufacturer address',
  manufacturer_warehouse: 'Manufacturer warehouse',
  manufacturer_override: 'Manufacturer ship-from',
  product: 'Product ship-from',
  operator_override: 'Set by staff',
  runtime_fallback: 'Manufacturer address, not saved as a ship-from',
  store_default: 'Our default, no manufacturer address',
}

const MODE_REASONS: Record<string, string> = {
  within_parcel_limits: 'Fits parcel size and weight limits',
  inferred_packaged_facts_within_parcel_limits: 'Fits parcel limits by estimated size and weight',
  inferred_dimensions_within_parcel_limits: 'Fits parcel limits by estimated size',
  inferred_weight_within_parcel_limits: 'Fits parcel limits by estimated weight',
  exceeds_parcel_limits: 'Too large or heavy for parcel',
  heavy_without_dimensions: 'Heavy with no box size on file',
  missing_packaged_facts: 'No box size or weight on file',
  missing_active_shipping_profile: 'No box size or weight on file',
  operator_override: 'Set by staff',
}

/** Why it ships this way. A limit met or missed on an estimated size or weight must say so (USA-1728). */
function modeReason(facts: Facts) {
  const reason = facts.classification_reason
  if (facts.size_source === 'Placeholder') return 'No size on file, so it quotes as freight'
  if (reason === 'exceeds_parcel_limits' && (facts.size_estimated || facts.weight_estimated)) {
    return 'Estimated size or weight is over parcel limits'
  }
  if (reason === 'within_parcel_limits' || reason.startsWith('inferred_')) {
    if (facts.size_estimated && facts.weight_estimated) {
      return MODE_REASONS.inferred_packaged_facts_within_parcel_limits
    }
    if (facts.size_estimated) return MODE_REASONS.inferred_dimensions_within_parcel_limits
    if (facts.weight_estimated) return MODE_REASONS.inferred_weight_within_parcel_limits
  }
  return MODE_REASONS[reason] ?? formatStatus(reason)
}

/** What the facts badge says: measured, partly estimated, or estimated. */
const FACT_QUALITY: Record<string, { label: string; variant: 'secondary' | 'outline' }> = {
  exact: { label: 'Measured', variant: 'secondary' },
  medium: { label: 'Partly estimated', variant: 'outline' },
  low: { label: 'Estimated', variant: 'outline' },
}

const SITE_LABELS: Record<string, string> = {
  COMMERCIAL: 'Commercial',
  RESIDENTIAL: 'Residential',
  LIMITED_ACCESS: 'Limited access',
}

const BASIS_LABELS: Record<string, string> = {
  manufacturer_free: 'Manufacturer pays freight',
  product_free: 'Free shipping product',
  manufacturer_percentage: 'Manufacturer freight program',
}

function day(iso: string) {
  return weekdayLabel(dayNumber(iso))
}

function serviceLabel(service: Service, mode: Estimate['mode']) {
  if (service.tier === 'liftgate') return 'Freight with liftgate'
  if (service.tier === 'two_day') return '2nd Day Air'
  if (service.tier === 'next_day') return 'Next Day Air'
  return mode === 'ltl' ? 'Freight' : 'Ground'
}

function leadTimeSource(lead: LeadTime) {
  if (lead.level === 'product') return 'Product setting'
  if (lead.level === 'category') return `${lead.category_name ?? 'Category'} rule`
  if (lead.level === 'manufacturer') return 'Manufacturer setting'
  return 'Default, none set'
}

function leadTimeExplanation(lead: LeadTime, manufacturer: string) {
  if (lead.level === 'product') {
    return 'Business days the manufacturer takes to ship this product, set on the product itself.'
  }
  if (lead.level === 'category') {
    return `${manufacturer}'s lead time for ${lead.category_name ?? 'this category'}. A lead time set on the product would replace it.`
  }
  if (lead.level === 'manufacturer') {
    return `${manufacturer}'s lead time. A lead time set on the product or its category would replace it.`
  }
  return `No lead time is set for this product, its category or ${manufacturer}, so the ${lead.days}-day default applies.`
}

function Fact({ label, value, source }: { label: string; value: string; source: string }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
      <dd className="text-xs text-muted-foreground">{source}</dd>
    </div>
  )
}

function DestinationForm({
  loading,
  onSubmit,
}: {
  loading: boolean
  onSubmit: (body: components['schemas']['ProductShippingQuoteRequest']) => void
}) {
  const id = useId()
  const [fullAddress, setFullAddress] = useState(false)
  const [address, setAddress] = useState({ address1: '', city: '', state: '', postal: '' })
  const zipValid = /^\d{5}(?:-\d{4})?$/.test(address.postal.trim())
  const addressValid = !fullAddress || (address.address1.trim() && address.city.trim() && address.state.trim())
  const set = (key: keyof typeof address) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setAddress((value) => ({ ...value, [key]: event.target.value }))

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({
      destination_postal_code: address.postal.trim(),
      ...(fullAddress
        ? { address1: address.address1.trim(), city: address.city.trim(), state: address.state.trim() }
        : {}),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Delivery destination">
      <div className="flex flex-wrap items-end gap-3">
        {fullAddress ? (
          <>
            <div className="grid min-w-0 basis-full gap-1.5 sm:basis-auto sm:flex-[2_1_16rem]">
              <Label htmlFor={`${id}-street`} className="text-xs text-muted-foreground">
                Street address
              </Label>
              <InlineFieldInput id={`${id}-street`} autoComplete="address-line1" value={address.address1} onChange={set('address1')} className="h-11 sm:h-9" />
            </div>
            <div className="flex min-w-0 basis-full gap-3 sm:contents">
              <div className="grid min-w-0 flex-1 gap-1.5 sm:flex-[1_1_9rem]">
                <Label htmlFor={`${id}-city`} className="text-xs text-muted-foreground">
                  City
                </Label>
                <InlineFieldInput id={`${id}-city`} autoComplete="address-level2" value={address.city} onChange={set('city')} className="h-11 sm:h-9" />
              </div>
              <div className="grid w-20 shrink-0 gap-1.5">
                <Label htmlFor={`${id}-state`} className="text-xs text-muted-foreground">
                  State
                </Label>
                <InlineFieldInput
                  id={`${id}-state`}
                  autoComplete="address-level1"
                  maxLength={20}
                  value={address.state}
                  onChange={set('state')}
                  className="h-11 sm:h-9"
                />
              </div>
            </div>
          </>
        ) : null}
        <div className="grid w-32 gap-1.5">
          <Label htmlFor={`${id}-zip`} className="text-xs text-muted-foreground">
            ZIP
          </Label>
          <InlineFieldInput
            id={`${id}-zip`}
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={10}
            placeholder="75201"
            value={address.postal}
            onChange={set('postal')}
            className="h-11 sm:h-9"
          />
        </div>
        <LoadingButton
          type="submit"
          loading={loading}
          loadingText="Quoting..."
          disabled={!zipValid || !addressValid}
          className="h-11 sm:h-9"
        >
          Get estimate
        </LoadingButton>
      </div>
      <button
        type="button"
        onClick={() => setFullAddress((value) => !value)}
        className="rounded text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {fullAddress ? 'Use ZIP only' : 'Use full address'}
      </button>
    </form>
  )
}

function ServiceOptions({
  estimate,
  selected,
  onSelect,
}: {
  estimate: Estimate
  selected: Service['tier']
  onSelect: (tier: Service['tier']) => void
}) {
  const name = useId()
  const manufacturerPays = estimate.pricing_basis === 'manufacturer_free'
  return (
    <fieldset className="min-w-0">
      <legend className="sr-only">Shipping service</legend>
      <div className="hidden grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_7rem_6rem_6rem] gap-3 border-b px-3 pb-1.5 text-[11px] font-medium text-muted-foreground lg:grid">
        <span>Service</span>
        <span>Arrives</span>
        <span>Transit</span>
        <span className="text-right">Price</span>
        <span className="text-right">Net</span>
      </div>
      <ul className="divide-y">
        {estimate.services.map((service) => {
          const checked = service.tier === selected
          return (
            <li key={service.tier}>
              <label
                className={cn(
                  'grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-3 py-2.5 text-sm transition-colors hover:bg-muted/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/50 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_7rem_6rem_6rem]',
                  checked && 'bg-muted/60',
                )}
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  <input
                    type="radio"
                    name={name}
                    value={service.tier}
                    checked={checked}
                    onChange={() => onSelect(service.tier)}
                    className="size-4 shrink-0 accent-foreground"
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">{serviceLabel(service, estimate.mode)}</span>
                    {service.carrier_name || service.service_name ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {[service.carrier_name, service.service_name].filter(Boolean).join(' · ')}
                      </span>
                    ) : null}
                  </span>
                </span>
                <span className="col-start-1 row-start-2 pl-6.5 text-xs text-muted-foreground tabular-nums lg:col-start-auto lg:row-start-auto lg:pl-0 lg:text-sm lg:text-foreground">
                  {service.arrives_on ? day(service.arrives_on) : '—'}
                  <span className="lg:hidden">
                    {service.transit_days != null ? ` · ${service.transit_days}d in transit` : ''}
                  </span>
                </span>
                <span className="hidden tabular-nums text-muted-foreground lg:block">
                  {service.transit_days != null ? `${service.transit_days} business days` : '—'}
                </span>
                <span className="col-start-2 row-start-1 text-right font-semibold tabular-nums lg:col-start-auto lg:row-start-auto">
                  {manufacturerPays && service.tier === 'ground' ? 'Free' : formatMoney(service.price)}
                  {service.source === 'fallback' ? (
                    <span className="block text-[11px] font-normal text-muted-foreground">Rate card</span>
                  ) : null}
                </span>
                <span className="col-start-2 row-start-2 text-right text-xs tabular-nums text-muted-foreground lg:col-start-auto lg:row-start-auto lg:text-sm">
                  <span className="lg:hidden">Net </span>
                  {formatMoney(service.net)}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </fieldset>
  )
}

function CarrierQuotes({ estimate }: { estimate: Estimate }) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  const offers = estimate.carrier_offers ?? []
  // Parcel offers are the services above; freight carriers are the choice worth listing.
  if (offers.length <= estimate.services.length) return null
  const Icon = open ? ChevronUp : ChevronDown
  return (
    <div className="border-t pt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-9 items-center gap-1.5 rounded-md px-1 text-sm font-medium text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <Icon className="size-4" aria-hidden />
        {open ? 'Hide carrier quotes' : `All ${offers.length} carrier quotes`}
      </button>
      {open ? (
        <ul id={listId} className="mt-1 divide-y text-sm">
          {offers.map((offer, index) => (
            <li
              key={`${offer.carrier_name}-${offer.service_name}-${offer.liftgate ? 'lg' : 'std'}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 px-3 py-2 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_7rem_6rem_6rem]"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{offer.carrier_name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {offer.service_name}
                  {offer.liftgate ? ' · Liftgate' : ''}
                </span>
              </span>
              <span className="col-start-1 row-start-2 text-xs text-muted-foreground tabular-nums lg:col-start-auto lg:row-start-auto lg:text-sm lg:text-foreground">
                {offer.arrives_on ? day(offer.arrives_on) : '—'}
              </span>
              <span className="hidden tabular-nums text-muted-foreground lg:block">
                {offer.transit_days != null ? `${offer.transit_days} business days` : '—'}
              </span>
              <span className="col-start-2 row-start-1 text-right tabular-nums lg:col-start-auto lg:row-start-auto">
                {formatMoney(offer.price)}
              </span>
              <span className="col-start-2 row-start-2 text-right text-xs tabular-nums text-muted-foreground lg:col-start-auto lg:row-start-auto lg:text-sm">
                <span className="lg:hidden">Net </span>
                {formatMoney(offer.net)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function shipmentSummary(estimate: Estimate) {
  const units =
    estimate.mode === 'parcel'
      ? `${formatNumber(estimate.package_count)} ${estimate.package_count === 1 ? 'box' : 'boxes'}`
      : `${formatNumber(estimate.pallet_count)} ${estimate.pallet_count === 1 ? 'pallet' : 'pallets'}`
  const to = [estimate.destination_address1, `${estimate.destination_city}, ${estimate.destination_state} ${estimate.destination_postal_code}`]
    .filter(Boolean)
    .join(', ')
  return `${estimate.mode === 'ltl' ? 'Freight' : 'Parcel'} · ${units} · ${estimate.origin_city}, ${estimate.origin_state} to ${to}`
}

function DeliveryEstimate({
  productId,
  facts,
  manufacturerName,
}: {
  productId: string
  facts: Facts
  manufacturerName: string
}) {
  const quote = useProductShippingQuote(productId)
  const [selectedTier, setSelectedTier] = useState<Service['tier'] | null>(null)
  const estimate = quote.data
  const selected =
    estimate?.services.find((service) => service.tier === selectedTier) ?? estimate?.services[0] ?? null

  const today = estimate ? dayNumber(estimate.ordered_on) : todayNumber()
  const processingDone = estimate
    ? dayNumber(estimate.processing_done_on)
    : addBusinessDays(today, facts.processing_days)
  const lead = estimate?.lead_time ?? facts.lead_time
  const ships = estimate ? dayNumber(estimate.ships_on) : addBusinessDays(processingDone, lead.days)
  const arrival = selected?.arrives_on ? dayNumber(selected.arrives_on) : null
  const origin = estimate
    ? `${estimate.origin_city}, ${estimate.origin_state}`
    : [facts.origin_city, facts.origin_state].filter(Boolean).join(', ') || facts.origin_postal_code

  const steps: TimelineStep[] = [
    {
      key: 'processing',
      label: 'Processing',
      days: facts.processing_days,
      start: today,
      end: processingDone,
      source: 'USA Restaurant Suppliers',
      date: `PO sent ${weekdayLabel(processingDone)}`,
      explanation: 'USA Restaurant Suppliers checks the order and sends the PO to the manufacturer the next business day.',
    },
    {
      key: 'lead',
      label: 'Manufacturer lead time',
      days: lead.days,
      start: processingDone,
      end: ships,
      source: leadTimeSource(lead),
      date: `Ships ${weekdayLabel(ships)}`,
      explanation: leadTimeExplanation(lead, manufacturerName),
    },
    {
      key: 'transit',
      label: 'Shipping',
      days: selected?.transit_days ?? null,
      start: ships,
      end: arrival,
      source: selected
        ? [selected.carrier_name, selected.service_name].filter(Boolean).join(' · ') ||
          serviceLabel(selected, estimate?.mode ?? 'parcel')
        : 'Enter a ZIP to quote',
      date: arrival !== null ? `Arrives ${weekdayLabel(arrival)}` : null,
      explanation: selected
        ? `The carrier's quoted transit from ${origin} to ${estimate?.destination_city}, ${estimate?.destination_state}, counted from the day it ships.`
        : 'Carrier transit time comes from a live quote to the destination.',
    },
  ]

  return (
    <section aria-labelledby="delivery-estimate" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b px-4 py-3">
        <h2 id="delivery-estimate" className="text-sm font-semibold">
          Delivery estimate
        </h2>
        <p className="text-sm" aria-live="polite">
          {arrival !== null ? (
            <>
              <span className="text-muted-foreground">Arrives </span>
              <span className="font-semibold">{weekdayLabel(arrival)}</span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">Ships </span>
              <span className="font-semibold">{weekdayLabel(ships)}</span>
            </>
          )}
        </p>
      </div>
      <div className="space-y-5 px-4 py-4">
        {facts.quote_available ? (
          <DestinationForm loading={quote.isPending} onSubmit={(body) => quote.mutate(body, { onSuccess: () => setSelectedTier(null) })} />
        ) : (
          <p className="text-sm text-muted-foreground">{facts.quote_unavailable_reason}</p>
        )}
        {quote.isError ? (
          <p className="flex gap-2 text-sm text-destructive" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {getApiErrorMessage(quote.error, 'Could not get a shipping quote. Check the destination and try again.')}
          </p>
        ) : null}

        <ProductDeliveryTimeline steps={steps} arrival={arrival} today={today} />

        {estimate ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>{shipmentSummary(estimate)}</span>
              {estimate.mode === 'ltl' && estimate.delivery_site ? (
                <span>
                  {SITE_LABELS[estimate.delivery_site]} delivery
                  {estimate.delivery_site_confirmed ? '' : ' (assumed from ZIP)'}
                </span>
              ) : null}
              {BASIS_LABELS[estimate.pricing_basis] ? (
                <Badge variant="secondary">{BASIS_LABELS[estimate.pricing_basis]}</Badge>
              ) : null}
            </div>
            <ServiceOptions estimate={estimate} selected={selected?.tier ?? 'ground'} onSelect={setSelectedTier} />
            <CarrierQuotes estimate={estimate} />
            {(estimate.warnings ?? []).map((warning) => (
              <p key={warning} className="flex gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {warning}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  )
}

function PackageFacts({ facts, freeShipping }: { facts: Facts; freeShipping?: boolean | null }) {
  const provenance = record(facts.provenance)
  const estimate = record(provenance.estimate)
  const dimensionsSource = provenance.dimensions
  const fieldSource = (field: 'length_in' | 'width_in' | 'height_in') =>
    facts.size_source ??
    sourceLabel(dimensionsSource === 'estimate_snapshot' ? record(estimate[field]).source : dimensionsSource)
  const weightSource =
    facts.weight_source ??
    sourceLabel(provenance.weight === 'estimate_snapshot' ? record(estimate.weight_lbs).source : provenance.weight)
  const freightClassSource =
    provenance.freight_class === 'exact_or_snapshot'
      ? record(estimate.freight_class).source || provenance.freight_class
      : provenance.freight_class
  // A class worked out from an estimated size or weight is itself an estimate.
  const freightSource =
    freightClassSource === 'density' && (facts.size_estimated || facts.weight_estimated)
      ? 'Estimated from size'
      : sourceLabel(freightClassSource)
  const quality = FACT_QUALITY[facts.confidence] ?? FACT_QUALITY.low!
  const usesSimilar = facts.taxonomy_cohort_name && (facts.peer_sample_size ?? 0) > 0

  return (
    <section aria-labelledby="package-facts" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 id="package-facts" className="text-sm font-semibold">
            Package
          </h2>
          <p className="text-xs text-muted-foreground">{modeReason(facts)}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={facts.classification_mode === 'unknown' ? 'outline' : 'secondary'}>
            {facts.classification_mode === 'small_parcel'
              ? 'Parcel'
              : facts.classification_mode === 'ltl'
                ? 'Freight'
                : 'Unknown'}
          </Badge>
          {facts.has_override ? (
            <Badge>Set by staff</Badge>
          ) : (
            <Badge variant={quality.variant}>{facts.size_source === 'Placeholder' ? 'Placeholder' : quality.label}</Badge>
          )}
          {freeShipping ? <Badge>Free shipping</Badge> : null}
        </div>
      </div>
      <div className="space-y-4 px-4 py-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3 lg:grid-cols-6">
          <Fact label="Length" value={`${formatNumber(facts.length_in)} in`} source={fieldSource('length_in')} />
          <Fact label="Width" value={`${formatNumber(facts.width_in)} in`} source={fieldSource('width_in')} />
          <Fact label="Height" value={`${formatNumber(facts.height_in)} in`} source={fieldSource('height_in')} />
          <Fact label="Weight" value={`${formatNumber(facts.weight_lbs)} lb`} source={weightSource} />
          <Fact label="Freight class" value={facts.freight_class} source={freightSource} />
          <Fact
            label="Boxes"
            value={formatNumber(facts.packages_per_unit)}
            source={facts.packages_per_unit === 1 ? 'Per unit' : 'Per selling unit'}
          />
        </dl>
        <dl className="grid gap-x-6 gap-y-3 border-t pt-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div className="min-w-0">
            <dt className="text-xs text-muted-foreground">Ships from</dt>
            <dd className="font-medium">
              {[facts.origin_city, [facts.origin_state, facts.origin_postal_code].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ')}
            </dd>
            <dd className="text-xs text-muted-foreground">
              {ORIGIN_LABELS[facts.origin_source] ?? formatStatus(facts.origin_source)}
            </dd>
          </div>
          {usesSimilar ? (
            <div className="min-w-0">
              <dt className="text-xs text-muted-foreground">Similar products</dt>
              <dd className="font-medium">{facts.taxonomy_cohort_name}</dd>
              <dd className="text-xs text-muted-foreground">
                {formatNumber(facts.peer_sample_size)} products · {facts.estimator_percentile}th percentile
              </dd>
            </div>
          ) : null}
          {facts.density_lbs_per_cuft != null ? (
            <div className="min-w-0">
              <dt className="text-xs text-muted-foreground">Density</dt>
              <dd className="font-medium">{formatNumber(facts.density_lbs_per_cuft)} lb/ft³</dd>
              <dd className="text-xs text-muted-foreground">
                {formatNumber(facts.density_sample_size)} products · {facts.density_percentile}th percentile
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </section>
  )
}

/** The product's Shipping tab: when an order arrives and what each way of shipping costs, then its package facts (USA-1499). */
export function ProductShippingCard({
  productId,
  manufacturerName,
  freeShipping,
}: {
  productId: string
  manufacturerName: string
  freeShipping?: boolean | null
}) {
  const facts = useProductShippingFacts(productId)

  if (facts.isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />
  }

  if (facts.isError || !facts.data) {
    return (
      <div className="flex items-center justify-between gap-4 rounded-xl border bg-card px-4 py-6">
        <p className="text-sm text-destructive">Could not load shipping facts.</p>
        <LoadingButton variant="outline" size="sm" onClick={() => void facts.refetch()}>
          Try again
        </LoadingButton>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="product-shipping-card">
      <DeliveryEstimate productId={productId} facts={facts.data} manufacturerName={manufacturerName} />
      <PackageFacts facts={facts.data} freeShipping={freeShipping} />
    </div>
  )
}
